import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Star, Eye, Shield, Calendar, Mail } from 'lucide-react';
import { usePageSeo } from '../hooks/usePageSeo';

interface Props {
  darkMode: boolean;
}

const SponsorPage: React.FC<Props> = ({ darkMode }) => {
  usePageSeo({
    title: 'Sponsorlu Forum Listeleme — Şeffaf Sponsorluk',
    description:
      'Forum listesinde sponsorlu görünürlük: şeffaf etiket, bitiş tarihi ve tıklama takibi. Türk forum istatistikleri sitesi Kitapçık.',
    keywords: 'sponsorlu forum, forum reklam, forum tanıtım',
    canonical: '/sponsor',
  });

  const card = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm';
  const text = darkMode ? 'text-gray-300' : 'text-gray-600';
  const heading = darkMode ? 'text-white' : 'text-gray-900';

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Link
          to="/"
          className={`inline-flex items-center gap-2 mb-6 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
        >
          <ArrowLeft size={18} />
          Anasayfa
        </Link>

        <header className={`rounded-2xl border p-6 sm:p-8 mb-6 ${card}`}>
          <div className="flex items-center gap-3 mb-4">
            <Star className="text-amber-500 fill-amber-500/30" size={36} />
            <h1 className={`text-2xl sm:text-3xl font-bold ${heading}`}>Sponsorlu listeleme</h1>
          </div>
          <p className={`text-sm leading-relaxed ${text}`}>
            Sponsorlu forumlar listede öne çıkar; tüm ziyaretçilere <strong className={heading}>açıkça “Sponsorlu”</strong>{' '}
            etiketi gösterilir. Bitiş tarihi (`sponsored_until`) dolunca sponsorluk otomatik olarak görünmez.
          </p>
        </header>

        <div className="grid gap-4 sm:grid-cols-3 mb-8">
          {[
            { icon: Eye, title: 'Şeffaflık', desc: 'Sponsor içerik her kartta ve detayda işaretlenir; gizli reklam yok.' },
            { icon: Calendar, title: 'Süre sonu', desc: 'Admin panelinden bitiş tarihi atanır; süre bitince rozet kalkar.' },
            { icon: Shield, title: 'Güven', desc: 'İstatistikler scrape ile bağımsızdır; sponsorluk sıralamayı şeffaf şekilde etkiler.' },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className={`rounded-xl border p-4 ${card}`}>
              <Icon className="text-indigo-500 mb-2" size={24} />
              <h2 className={`font-semibold mb-1 ${heading}`}>{title}</h2>
              <p className={`text-sm ${text}`}>{desc}</p>
            </div>
          ))}
        </div>

        <section className={`rounded-xl border p-6 ${card}`}>
          <h2 className={`text-lg font-semibold mb-3 ${heading}`}>Nerede görünür?</h2>
          <ul className={`list-disc pl-5 space-y-2 text-sm ${text}`}>
            <li>Anasayfa forum listesinde (sponsorlu forumlar üst sıralarda)</li>
            <li>Kategori ve yazılım listelerinde</li>
            <li>Forum detay sayfasında sponsor rozeti</li>
          </ul>

          <h2 className={`text-lg font-semibold mt-6 mb-3 ${heading}`}>Sponsor olmak için</h2>
          <p className={`text-sm ${text}`}>
            Forumunuzu öne çıkarmak için site yöneticisi ile iletişime geçin. Süre ve koşullar birlikte belirlenir;
            yayına alındığında bitiş tarihi sitede gösterilir.
          </p>
          <a
            href="mailto:iletisim@kitapcik.net?subject=Forum%20sponsorlu%C4%9Fu"
            className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
          >
            <Mail size={18} />
            İletişim (e-posta)
          </a>
        </section>

        <p className={`mt-6 text-xs text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          <Link to="/new-forums?tab=suggest" className="text-indigo-500 hover:underline">
            Forum öner
          </Link>
          {' · '}
          <Link to="/api-docs" className="text-indigo-500 hover:underline">
            API / RSS
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SponsorPage;
